import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BsaPlayerField from './bsaPlayerField';
import GameFormatDescription from '../gameFormats/description';
import OverrideGameFormat from '../gameFormats/override';
import PoweredByBowlscout from '../ui/poweredByBowlscout';
import gameFormatService from '../../features/gameFormat/gameFormatService';
import { useColorMode } from '../../hooks/colorModeProvider';
import {
  QUICK_GAME_SCORING_METHODS,
  SCORING_METHOD_DESCRIPTIONS,
  SCORING_METHOD_LABELS
} from '../../constants/scoringMethods';

const TEAM_A_COLOR = 'red';
const TEAM_B_COLOR = 'green';

const emptySlot = () => ({ bsaNumber: '', player: null, status: 'idle' });

const buildTeamLabel = (slots) =>
  slots
    .map((slot) => (slot.player ? `${slot.player.name} ${slot.player.surname}` : ''))
    .filter(Boolean)
    .join(', ');

const QuickGameCreate = () => {
  const navigate = useNavigate();
  const { mode, setMode } = useColorMode();

  const [gameFormats, setGameFormats] = useState([]);
  const [formatsLoading, setFormatsLoading] = useState(true);
  const [formatsError, setFormatsError] = useState(null);

  const [selectedFormat, setSelectedFormat] = useState(null);
  const [baseFormat, setBaseFormat] = useState(null);

  const [creatorBsa, setCreatorBsa] = useState('');
  const [creatorPlayer, setCreatorPlayer] = useState(null);
  const [creatorStatus, setCreatorStatus] = useState('idle');

  const [gameName, setGameName] = useState('');

  const [teamA, setTeamA] = useState([]);
  const [teamB, setTeamB] = useState([]);

  const [scoringMethod, setScoringMethod] = useState('endbyend');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    setMode('light');
  }, [mode, setMode]);

  // Load global game formats (public endpoint).
  useEffect(() => {
    setFormatsLoading(true);
    fetch('/api/gameformat/public')
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load game formats');
        return res.json();
      })
      .then((formats) => {
        setGameFormats(Array.isArray(formats) ? formats : []);
        setFormatsLoading(false);
      })
      .catch((err) => {
        setFormatsError(err.message);
        setFormatsLoading(false);
      });
  }, []);

  const resetTeams = useCallback(
    (limit) => {
      const buildTeam = (size) => Array.from({ length: size }, emptySlot);

      const nextTeamA = buildTeam(limit);
      const nextTeamB = buildTeam(limit);

      // Creator is locked into Team A slot 1.
      if (creatorStatus === 'valid' && creatorPlayer) {
        nextTeamA[0] = {
          bsaNumber: creatorBsa,
          player: creatorPlayer,
          status: 'valid'
        };
      }

      setTeamA(nextTeamA);
      setTeamB(nextTeamB);
    },
    [creatorBsa, creatorPlayer, creatorStatus]
  );

  const handleFormatChange = (format) => {
    setBaseFormat(format);
    setSelectedFormat(format);
    resetTeams(gameFormatService.getGameTypeLimit(format?.gameType || 1) / 2);
  };

  const handleCreatorResolved = useCallback((bsaNumber, player, status) => {
    const nextStatus = status ?? 'idle';
    setCreatorStatus(nextStatus);
    setCreatorPlayer(player);

    // Keep the locked Team A slot 1 in sync with the creator field.
    setTeamA((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      next[0] =
        nextStatus === 'valid' && player
          ? { bsaNumber, player, status: 'valid' }
          : emptySlot();
      return next;
    });
  }, []);

  const handleSlotResolved = (teamSetter, index) => (bsaNumber, player, status) => {
    teamSetter((prev) => {
      const next = [...prev];
      next[index] = { bsaNumber, player, status: status ?? 'idle' };
      return next;
    });
  };

  const allValid = () => {
    if (!selectedFormat) return false;
    if (creatorStatus !== 'valid' || !creatorPlayer) return false;

    const slots = [...teamA, ...teamB];
    if (slots.length === 0 || slots.some((slot) => slot.status !== 'valid')) return false;

    // No duplicate BSA numbers across all slots.
    const bsas = slots.map((slot) => slot.bsaNumber);
    return new Set(bsas).size === bsas.length;
  };

  const handleSubmit = async () => {
    if (submitting) return;

    if (!selectedFormat) {
      setSubmitError('Select a game format first.');
      return;
    }

    if (!allValid()) {
      setSubmitError('Fill in a valid BSA number for every player slot.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    const payload = {
      name:
        gameName.trim() ||
        `${buildTeamLabel(teamA)} vs ${buildTeamLabel(teamB)}`,
      gameFormat: selectedFormat,
      gameType: selectedFormat.gameType,
      scoringMethod,
      teams: {
        teamA: {
          players: teamA.map((slot) => `${slot.player.name} ${slot.player.surname}`),
          members: teamA.map((slot) => ({
            name: slot.player.name,
            surname: slot.player.surname,
            bsaNumber: slot.bsaNumber
          })),
          color: TEAM_A_COLOR,
          name: buildTeamLabel(teamA)
        },
        teamB: {
          players: teamB.map((slot) => `${slot.player.name} ${slot.player.surname}`),
          members: teamB.map((slot) => ({
            name: slot.player.name,
            surname: slot.player.surname,
            bsaNumber: slot.bsaNumber
          })),
          color: TEAM_B_COLOR,
          name: buildTeamLabel(teamB)
        }
      }
    };

    try {
      const res = await fetch('/api/game/public', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to create game');
      }

      navigate(`/public/quickgames/${encodeURIComponent(creatorBsa)}`);
    } catch (err) {
      setSubmitError(err.message);
      setSubmitting(false);
    }
  };

  const renderTeamSection = (title, color, slots, setter) => {
    const validCount = slots.filter((slot) => slot.status === 'valid').length;

    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }} color={color}>
          {title} ({validCount}/{slots.length} players)
        </Typography>
        <Stack spacing={1.5}>
          {slots.map((slot, index) => (
            <BsaPlayerField
              key={index}
              label={`Player ${index + 1} BSA number`}
              value={slot.bsaNumber}
              player={slot.player}
              status={slot.status}
              disabled={title.startsWith('Team A') && index === 0}
              onChange={(value) =>
                setter((prev) => {
                  const next = [...prev];
                  next[index] = { ...next[index], bsaNumber: value };
                  return next;
                })
              }
              onResolved={handleSlotResolved(setter, index)}
            />
          ))}
        </Stack>
      </Paper>
    );
  };

  return (
    <Box
      sx={{
        maxWidth: 480,
        mx: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>
      <Box textAlign={'center'}>
        <Typography variant="h5" fontWeight="bold">
          Quick Game
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Create a game in seconds - no login needed.
        </Typography>
      </Box>

      <BsaPlayerField
        label="Your BSA number"
        value={creatorBsa}
        player={creatorPlayer}
        status={creatorStatus}
        onChange={setCreatorBsa}
        onResolved={handleCreatorResolved}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
        Used to find your games afterwards.
      </Typography>

      <TextField
        label="Game Name (Optional)"
        value={gameName}
        onChange={(event) => setGameName(event.target.value)}
        size="small"
        fullWidth
        helperText="Leave empty to use the player names."
      />

      <FormControl fullWidth size="small">
        <InputLabel id="format-select-label">Game Format</InputLabel>
        <Select
          labelId="format-select-label"
          label="Game Format"
          value={selectedFormat?.id ?? ''}
          onChange={(event) => {
            const format = gameFormats.find((gf) => gf.id === event.target.value);
            if (format) handleFormatChange(format);
          }}
          disabled={formatsLoading || formatsError}>
          {gameFormats.map((format) => (
            <MenuItem key={format.id} value={format.id}>
              {format.name} - {gameFormatService.getGameTypeString(format.gameType)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {formatsLoading && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
      {formatsError && (
        <Alert severity="error">Could not load game formats: {formatsError}</Alert>
      )}
      {!formatsLoading && !formatsError && gameFormats.length === 0 && (
        <Alert severity="info">No game formats available yet.</Alert>
      )}

      {selectedFormat && (
        <>
          <GameFormatDescription gameFormat={selectedFormat} />
          <OverrideGameFormat
            gameFormat={selectedFormat}
            baseGameFormat={baseFormat}
            onChange={(gf) => setSelectedFormat(gf)}
          />
        </>
      )}

      {selectedFormat && (
        <>
          {renderTeamSection('Team A', TEAM_A_COLOR, teamA, setTeamA)}
          {renderTeamSection('Team B', TEAM_B_COLOR, teamB, setTeamB)}
        </>
      )}

      {selectedFormat && (
        <FormControl fullWidth size="small">
          <InputLabel id="scoring-method-label">Scoring Method</InputLabel>
          <Select
            labelId="scoring-method-label"
            label="Scoring Method"
            value={scoringMethod}
            onChange={(event) => setScoringMethod(event.target.value)}>
            {QUICK_GAME_SCORING_METHODS.map((method) => (
              <MenuItem key={method} value={method}>
                <Box>
                  <Typography variant="body2">{SCORING_METHOD_LABELS[method]}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {SCORING_METHOD_DESCRIPTIONS[method]}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {submitError && <Alert severity="error">{submitError}</Alert>}

      <Button
        variant="contained"
        color="secondary"
        size="large"
        fullWidth
        disabled={submitting || formatsLoading || !selectedFormat}
        onClick={handleSubmit}>
        {submitting ? <CircularProgress size={22} /> : 'Create Game'}
      </Button>

      <PoweredByBowlscout />
    </Box>
  );
};

export default QuickGameCreate;
